/**
 * Timezone-agnostic date/time utilities for shifts and schedules
 * 
 * These utilities ensure that times are stored and displayed as entered,
 * regardless of the user's timezone. For example, a shift from 09:00-17:00
 * will always display as 09:00-17:00, whether viewed in Athens or Amsterdam.
 */

/**
 * Converts a Date object to an ISO string WITHOUT timezone conversion
 * This preserves the local time values (e.g., 09:00 stays 09:00)
 * 
 * @param date - The date to convert
 * @returns ISO-like string in format: YYYY-MM-DDTHH:mm:ss (no Z suffix)
 */
export function toLocalISOString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Parses an ISO string as local time (ignoring any timezone info)
 * This ensures times are interpreted as-is, not converted from UTC
 * 
 * @param isoString - ISO string to parse
 * @returns Date object with the time values as specified
 */
export function fromLocalISOString(isoString: string): Date {
  // Remove timezone info if present (Z or +/-offset)
  const cleanString = isoString.replace(/Z|([+-]\d{2}:\d{2})$/, '');
  
  // Parse as local time
  const [datePart, timePart] = cleanString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds = 0] = (timePart || '00:00:00').split(':').map(Number);
  
  return new Date(year, month - 1, day, hours, minutes, seconds);
}

/**
 * Formats a date for display in HH:mm format (24-hour)
 * 
 * @param date - Date to format
 * @returns Time string in HH:mm format
 */
export function formatTimeOnly(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Formats a date range for display
 * 
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Formatted string like "09:00-17:00"
 */
export function formatTimeRange(startDate: Date, endDate: Date): string {
  return `${formatTimeOnly(startDate)}-${formatTimeOnly(endDate)}`;
}
